import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { TrendingUp, Users, Globe, Coins, Clock, Calendar, Shield, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
}

interface DashboardOverviewProps {
  company: Company | null;
}

interface RemoteWorkData {
  month: string;
  count: number;
}

interface KPIData {
  employeesAbroad: number;
  teleworkPercentage: number;
  totalPointsUsed: number;
  monthlyIncrease: number;
  totalWorkationDays: number;
  pendingApprovals: number;
  riskBreakdown: {
    low: number;
    medium: number;
    high: number;
  };
}

interface RecentActivity {
  id: string;
  type: 'submitted' | 'approved' | 'rejected';
  employeeName: string;
  destination: string;
  timestamp: string;
}

const DashboardOverview = ({ company }: DashboardOverviewProps) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState<RemoteWorkData[]>([]);
  const [kpiData, setKPIData] = useState<KPIData>({
    employeesAbroad: 0,
    teleworkPercentage: 0,
    totalPointsUsed: 0,
    monthlyIncrease: 0,
    totalWorkationDays: 0,
    pendingApprovals: 0,
    riskBreakdown: { low: 0, medium: 0, high: 0 }
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (company) {
      fetchDashboardData();
    }
  }, [company]);

  const fetchDashboardData = async () => {
    if (!company) return;

    try {
      // Fetch remote work requests for the last 6 months
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      const { data: allRequests } = await supabase.rpc('get_company_remote_work_requests');

      // Filter for approved requests within date range
      const remoteRequests = (allRequests || []).filter(req => 
        req.status === 'approved' && 
        new Date(req.created_at) >= sixMonthsAgo
      );

      // Process chart data
      const monthlyData: { [key: string]: number } = {};
      const months = [];
      
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthKey = date.toISOString().substring(0, 7);
        const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        months.push({ key: monthKey, label: monthLabel });
        monthlyData[monthKey] = 0;
      }

      remoteRequests?.forEach(request => {
        const monthKey = request.created_at.substring(0, 7);
        if (monthlyData[monthKey] !== undefined) {
          monthlyData[monthKey]++;
        }
      });

      const chartData = months.map(month => ({
        month: month.label,
        count: monthlyData[month.key]
      }));

      setChartData(chartData);

      // Fetch KPI data
      await fetchKPIData(allRequests || []);

      // Fetch recent activity
      await fetchRecentActivity(allRequests || []);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchKPIData = async (allRequests: any[]) => {
    if (!company) return;

    try {
      const todayStr = new Date().toISOString().split('T')[0];
      const currentYear = new Date().getFullYear();

      // Employees currently abroad
      const abroadEmployees = allRequests.filter(req =>
        req.hr_approval_status === 'approved' &&
        req.manager_approval_status === 'approved' &&
        req.start_date && req.start_date <= todayStr &&
        req.end_date && req.end_date >= todayStr
      );

      // Total employees who have done remote work this year
      const teleworkEmployees = allRequests.filter(req =>
        req.hr_approval_status === 'approved' &&
        req.manager_approval_status === 'approved' &&
        req.created_at && req.created_at.startsWith(currentYear.toString())
      );

      // Total workation days this year
      const approvedThisYear = allRequests.filter(req =>
        req.hr_approval_status === 'approved' &&
        req.manager_approval_status === 'approved' &&
        req.start_date && req.start_date.startsWith(currentYear.toString())
      );

      const totalWorkationDays = approvedThisYear.reduce((total, req) => {
        if (req.start_date && req.end_date) {
          const start = new Date(req.start_date);
          const end = new Date(req.end_date);
          const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
          return total + days;
        }
        return total;
      }, 0);

      // Pending approvals
      const pendingApprovals = allRequests.filter(req =>
        (req.manager_approval_status === 'pending') ||
        (req.manager_approval_status === 'approved' && req.hr_approval_status === 'pending')
      ).length;

      // Risk breakdown
      const riskBreakdown = { low: 0, medium: 0, high: 0 };
      abroadEmployees.forEach(req => {
        const level = req.risk_level || 'low';
        if (riskBreakdown[level as keyof typeof riskBreakdown] !== undefined) {
          riskBreakdown[level as keyof typeof riskBreakdown]++;
        }
      });

      // Total points used
      const { data: pointsData } = await supabase
        .from('employee_points')
        .select(`
          used_points,
          employee_id,
          employees!inner(company_id)
        `)
        .eq('employees.company_id', company.id);

      // Requests this month vs last month
      const thisMonth = new Date();
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);

      const thisMonthRequests = allRequests.filter(req =>
        req.status === 'approved' &&
        req.created_at && req.created_at >= thisMonth.toISOString().substring(0, 7) + '-01'
      );

      const lastMonthRequests = allRequests.filter(req =>
        req.status === 'approved' &&
        req.created_at && 
        req.created_at >= lastMonth.toISOString().substring(0, 7) + '-01' &&
        req.created_at < thisMonth.toISOString().substring(0, 7) + '-01'
      );

      const uniqueEmployeesAbroad = new Set(abroadEmployees?.map(r => r.employee_id)).size;
      const uniqueTeleworkEmployees = new Set(teleworkEmployees?.map(r => r.employee_id)).size;
      const totalPointsUsed = pointsData?.reduce((sum, p) => sum + p.used_points, 0) || 0;
      
      const thisMonthCount = thisMonthRequests.length;
      const lastMonthCount = lastMonthRequests.length;
      const monthlyIncrease = lastMonthCount === 0 ? 0 : 
        ((thisMonthCount - lastMonthCount) / lastMonthCount) * 100;

      setKPIData({
        employeesAbroad: uniqueEmployeesAbroad,
        teleworkPercentage: company.employee_count === 0 ? 0 : 
          Math.round((uniqueTeleworkEmployees / company.employee_count) * 100),
        totalPointsUsed,
        monthlyIncrease: Math.round(monthlyIncrease),
        totalWorkationDays,
        pendingApprovals,
        riskBreakdown
      });

    } catch (error) {
      console.error('Error fetching KPI data:', error);
    }
  };

  const fetchRecentActivity = async (allRequests: any[]) => {
    try {
      // Get last 5 activities
      const sortedRequests = [...allRequests]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 5);

      const activities: RecentActivity[] = await Promise.all(
        sortedRequests.map(async (req) => {
          const { data: emp } = await supabase
            .from('employees')
            .select('first_name, last_name')
            .eq('id', req.employee_id)
            .single();

          let type: 'submitted' | 'approved' | 'rejected' = 'submitted';
          if (req.hr_approval_status === 'approved') type = 'approved';
          else if (req.hr_approval_status === 'rejected' || req.manager_approval_status === 'rejected') type = 'rejected';

          return {
            id: req.id,
            type,
            employeeName: emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown',
            destination: req.destination_country || 'Unknown',
            timestamp: req.created_at
          };
        })
      );

      setRecentActivity(activities);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-8 bg-muted rounded w-3/4 mb-4"></div>
                <div className="h-12 bg-muted rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className="animate-pulse">
          <CardContent className="p-6">
            <div className="h-64 bg-muted rounded"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards - 4 columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("dashboard.kpi.employeesAbroad", "Employees Abroad")}
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {kpiData.employeesAbroad}
                </p>
              </div>
              <Globe className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("dashboard.kpi.pendingApprovals", "Pending Approvals")}
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {kpiData.pendingApprovals}
                </p>
              </div>
              <Clock className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("dashboard.kpi.totalWorkationDays", "Total Workation Days")}
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {kpiData.totalWorkationDays}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("dashboard.kpi.pointsUsed", "Nest Points Used")}
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {kpiData.totalPointsUsed}
                </p>
              </div>
              <Coins className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Risk Breakdown and Monthly Change */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="h-5 w-5" />
              {t("dashboard.kpi.riskBreakdown", "Risk Breakdown")}
            </CardTitle>
            <CardDescription>
              {t("dashboard.kpi.riskBreakdownDesc", "Employees currently abroad by risk level")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-sm">{t("dashboard.worldMap.legend.low", "Low Risk")}</span>
                  </div>
                  <Badge variant="outline" className="bg-green-100 text-green-700">
                    {kpiData.riskBreakdown.low}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <span className="text-sm">{t("dashboard.worldMap.legend.medium", "Medium Risk")}</span>
                  </div>
                  <Badge variant="outline" className="bg-yellow-100 text-yellow-700">
                    {kpiData.riskBreakdown.medium}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-sm">{t("dashboard.worldMap.legend.high", "High Risk")}</span>
                  </div>
                  <Badge variant="outline" className="bg-red-100 text-red-700">
                    {kpiData.riskBreakdown.high}
                  </Badge>
                </div>
              </div>
              {kpiData.riskBreakdown.high > 0 && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  <span className="text-sm text-red-700 font-medium">
                    {kpiData.riskBreakdown.high} at risk
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Monthly Increase Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-5 w-5" />
              {t("dashboard.kpi.monthlyChange", "Monthly Change")}
            </CardTitle>
            <CardDescription>
              {t("dashboard.kpi.comparedToLastMonth", "Compared to last month")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-4xl font-bold ${kpiData.monthlyIncrease >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {kpiData.monthlyIncrease >= 0 ? '+' : ''}{kpiData.monthlyIncrease}%
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("dashboard.kpi.teleworkPercentage", "Remote Work Rate")}: {kpiData.teleworkPercentage}%
                </p>
              </div>
              <TrendingUp className={`h-12 w-12 ${kpiData.monthlyIncrease >= 0 ? 'text-green-500' : 'text-red-500'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              {t("dashboard.chart.title", "Remote Work Trends")}
            </CardTitle>
            <CardDescription>
              {t("dashboard.chart.description", "Approved remote work requests over the last 6 months")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(label) => `${t("dashboard.chart.month", "Month")}: ${label}`}
                    formatter={(value) => [value, t("dashboard.chart.employees", "Employees")]}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("dashboard.recentActivity.title", "Recent Activity")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentActivity.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t("dashboard.recentActivity.noActivity", "No recent activity")}
              </p>
            ) : (
              recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    activity.type === 'approved' ? 'bg-green-500' :
                    activity.type === 'rejected' ? 'bg-red-500' : 'bg-blue-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{activity.employeeName}</p>
                    <p className="text-xs text-muted-foreground">
                      {activity.type === 'approved' ? t("dashboard.recentActivity.approved", "Approved") :
                       activity.type === 'rejected' ? t("dashboard.recentActivity.rejected", "Rejected") :
                       t("dashboard.recentActivity.submitted", "Submitted")} → {activity.destination}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(activity.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardOverview;
